import React from 'react'
import PropTypes from 'prop-types'
import { StatusTile } from '../../workOrders/components'
import {
  formatTripDuration,
  formatUTCAsLocalTime,
  groupTripQuestionResponses,
} from 'utils'
import * as Types from 'types'
import { workOrder as workOrderType } from 'types'

const { TRIP_QUESTION_SECTION, QUESTION_TYPE } = Types

const propTypes = {
  workOrder: workOrderType.isRequired,
  trip: Types.trip.isRequired,
  selectImage: PropTypes.func.isRequired,
  selectedImage: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    url: PropTypes.string,
    filename: PropTypes.string,
  }),
}
const defaultProps = {
  selectedImage: null,
}

function VisitContent({ workOrder, trip, selectImage, selectedImage }) {
  const {
    createdAt,
    checkedOutAt,
    notes,
    abuseNeglectClaims,
    operable,
    rootCause,
    status,
    allQuestionResponses = [],
  } = trip
  const abuseDefectHeaderText = Types.ROOT_CAUSE_NEGLECT_OPTIONS.includes(
    rootCause
  )
    ? 'Abuse Notes'
    : 'Defect Notes'

  const grouped = groupTripQuestionResponses(allQuestionResponses)
  const checkInQuestions = [
    ...grouped[TRIP_QUESTION_SECTION.CHECK_IN_STANDARD],
    ...grouped[TRIP_QUESTION_SECTION.CHECK_IN_CUSTOM],
  ]
  const checkOutStandardQuestions =
    grouped[TRIP_QUESTION_SECTION.CHECK_OUT_STANDARD]
  const checkOutCustomQuestions =
    grouped[TRIP_QUESTION_SECTION.CHECK_OUT_CUSTOM]

  return (
    <>
      <div className="manager-order-show__visit-meta">
        <span className="manager-order-show__visit-wo-name">
          {workOrder.name}
        </span>
        <StatusTile status={status} />
      </div>
      <div className="manager-order-show__visit-details">
        <div className="manager-order-show__detail">
          <span className="manager-order-show__detail-label">Checked In</span>
          <span className="manager-order-show__detail-value">
            {formatUTCAsLocalTime(createdAt) || '—'}
          </span>
        </div>
        <div className="manager-order-show__detail">
          <span className="manager-order-show__detail-label">Checked Out</span>
          <span className="manager-order-show__detail-value">
            {formatUTCAsLocalTime(checkedOutAt) || '—'}
          </span>
        </div>
        <div className="manager-order-show__detail">
          <span className="manager-order-show__detail-label">Duration</span>
          <span className="manager-order-show__detail-value">
            {formatTripDuration(trip)}
          </span>
        </div>
      </div>
      {/*
        Every section renders for every trip, whatever its status. Photos sync
        to Salesforce the moment they are uploaded, so a trip that was never
        checked out (closed no checkout, expired, or stalled on site) can still
        hold check-in photos and even checkout-phase photos. Missing checkout
        answers simply show as a dash.
      */}
      <VisitQuestionSection
        title="Check In Questions"
        questions={checkInQuestions}
        selectImage={selectImage}
        selectedImage={selectedImage}
      />

      <h4 className="manager-order-show__visit-section-title">
        Standard Checkout Questions
      </h4>
      <div className="manager-order-show__visit-details">
        <div className="manager-order-show__detail">
          <span className="manager-order-show__detail-label">Notes</span>
          <span className="manager-order-show__detail-value">
            {notes || '—'}
          </span>
        </div>
        {(Types.ROOT_CAUSE_NEGLECT_OPTIONS.includes(rootCause) ||
          rootCause === Types.ROOT_CAUSE_DEFECT) && (
          <div className="manager-order-show__detail">
            <span className="manager-order-show__detail-label">
              {abuseDefectHeaderText}
            </span>
            <span className="manager-order-show__detail-value">
              {abuseNeglectClaims || '—'}
            </span>
          </div>
        )}
        <div className="manager-order-show__detail">
          <span className="manager-order-show__detail-label">Root Cause</span>
          <span className="manager-order-show__detail-value">
            {rootCause || '—'}
          </span>
        </div>
        <div className="manager-order-show__detail">
          <span className="manager-order-show__detail-label">Operational?</span>
          <span className="manager-order-show__detail-value">
            {operable || '—'}
          </span>
        </div>
      </div>
      <VisitQuestionSection
        questions={checkOutStandardQuestions}
        selectImage={selectImage}
        selectedImage={selectedImage}
      />

      <VisitQuestionSection
        title="Custom Check Out Questions"
        questions={checkOutCustomQuestions}
        selectImage={selectImage}
        selectedImage={selectedImage}
      />
    </>
  )
}

function VisitQuestionSection({
  title,
  questions,
  selectImage,
  selectedImage,
}) {
  if (questions.length === 0) return null

  const photoEntries = questions
    .filter(
      (q) => q.type === QUESTION_TYPE.PHOTO && Array.isArray(q.questionResponse)
    )
    .flatMap((q) =>
      q.questionResponse.map((image) => ({
        questionText: q.questionText,
        image,
      }))
    )
  const otherQuestions = questions.filter(
    (q) => q.type !== QUESTION_TYPE.PHOTO || !Array.isArray(q.questionResponse)
  )

  return (
    <>
      {title && (
        <h4 className="manager-order-show__visit-section-title">{title}</h4>
      )}
      {photoEntries.length > 0 && (
        <div className="manager-order-show__visit-photos">
          <div className="manager-order-show__visit-photos-grid">
            {photoEntries.map(({ questionText, image }, index) => (
              <div
                key={image.id}
                className={`manager-order-show__photo-item ${
                  selectedImage?.id === image.id
                    ? 'manager-order-show__photo-item--selected'
                    : ''
                }`}
                role="button"
                tabIndex={0}
                onClick={() => selectImage(image)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    selectImage(image)
                  }
                }}
              >
                <div className="manager-order-show__photo-thumb">
                  <img alt={`Trip img ${index + 1}`} src={image.url} />
                </div>
                <div className="manager-order-show__photo-title">
                  {questionText}
                </div>
                <div className="manager-order-show__photo-filename">
                  {image.filename}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {otherQuestions.map((question, index) => (
        <div key={index} className="manager-order-show__visit-question">
          <div className="manager-order-show__question-text">
            {question.questionText}
          </div>
          <div className="manager-order-show__question-value">
            {question.questionResponse ?? '—'}
          </div>
        </div>
      ))}
    </>
  )
}

VisitContent.propTypes = propTypes
VisitContent.defaultProps = defaultProps

export default VisitContent
